import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BadgeCheckIcon, FileTextIcon, InboxIcon, PenLineIcon, UserIcon } from "lucide-react";
import { Link } from "@/core/lib/navigation";
import { portalMeQuery } from "@/lib/portal-data";
import { Button } from "@/ui/components/ui/button";
import { Card, CardContent } from "@/ui/components/ui/card";

const TILES = [
  {
    href: "/portal/requests",
    title: "Requests",
    description: "Request study leave, time off, or a document.",
    icon: InboxIcon,
  },
  {
    href: "/portal/documents",
    title: "Documents",
    description: "View and download your finalized documents.",
    icon: FileTextIcon,
  },
  {
    href: "/portal/details",
    title: "My details",
    description: "Review your profile and request updates.",
    icon: UserIcon,
  },
] as const;

export const Route = createFileRoute("/portal/")({
  component: PortalHomePage,
});

function PortalHomePage() {
  // Already resolved by the portal shell's loader — this reads the same entry.
  const { data: me } = useQuery(portalMeQuery);
  const firstName = me?.preferredName || me?.firstName || "there";
  const toSign = me?.counts?.signing ?? 0;
  const credentials = me?.counts?.credentials ?? 0;

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h1 className="font-heading font-semibold text-2xl tracking-tight">Hi {firstName} 👋</h1>
        <p className="text-muted-foreground text-sm">
          {me?.jobTitle ? `${me.jobTitle} · ` : ""}
          {me?.department ? `${me.department} · ` : ""}
          {me?.companyName ?? ""}
        </p>
      </div>
      {toSign > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50/50 p-4 sm:flex-row sm:items-center dark:border-amber-900/40 dark:bg-amber-950/20">
          <PenLineIcon className="size-5 shrink-0 text-amber-500" />
          <p className="flex-1 text-sm">
            {toSign === 1
              ? "A document is waiting for your signature."
              : `${toSign} documents are waiting for your signature.`}
          </p>
          <Button asChild size="sm">
            <Link href="/portal/documents">Review &amp; sign</Link>
          </Button>
        </div>
      )}
      {credentials > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-rose-200 bg-rose-50/50 p-4 sm:flex-row sm:items-center dark:border-rose-900/40 dark:bg-rose-950/20">
          <BadgeCheckIcon className="size-5 shrink-0 text-rose-500" />
          <p className="flex-1 text-sm">
            {credentials === 1
              ? "A license or certificate needs your attention."
              : `${credentials} licenses or certificates need your attention.`}
          </p>
          <Button asChild size="sm">
            <Link href="/portal/credentials">Review</Link>
          </Button>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <Link key={tile.href} href={tile.href} className="group block">
              <Card className="h-full transition-colors group-hover:border-foreground/20">
                <CardContent className="flex flex-col gap-3 p-5">
                  <span className="flex size-10 items-center justify-center rounded-lg bg-muted text-foreground">
                    <Icon className="size-5" />
                  </span>
                  <div className="space-y-1">
                    <h2 className="font-medium text-sm">{tile.title}</h2>
                    <p className="text-muted-foreground text-sm">{tile.description}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
